import { useMemo } from "react"
import { getValue, hasValue } from "../Models/Client/FetchState"
import { useJob } from "./useJob"
import { useJobResponse } from "./useJobResponse"
import { JobStatus } from "../Models/API/JobModel"
import { downloadAsJSON, downloadData } from "../Helpers/Functions/downloadAs"
import { useFeedbackMessages } from "./useFeedbackMessages"
import { prepareFeedbackForDownload } from "../Models/API/JobFeedbackModel"
import { useJobHistory } from "./useJobHistory"
import { useJobVersionResponse } from "./useJobVersionResponse"
import { findDataUrlForDownload } from "../Helpers/Schema/Schema"
import { useDispatch } from "react-redux"
import { warnMessage } from "../Actions/Client/MessagesActions"

export function useJobDownload(
    urlSigner: (url: string) => Promise<string>,
    appID: string,
    isJobRelabeled: boolean,
    selectedVersion: number,
    jobID?: string,
    imageDownloadPath?: string[],
    downloadResponeData?: boolean
): undefined | (() => Promise<void>) {
    const jobFetch = useJob(appID, jobID)
    const dispatch = useDispatch()
    const shouldFetchInfo =
        hasValue(jobFetch) && jobFetch.value.status === JobStatus.COMPLETED
    const jobResponseFetch = useJobResponse(
        appID,
        shouldFetchInfo ? jobID : undefined
    )
    const jobHistoryFetch = useJobHistory(jobID)

    const jobFeedbackMessages = useFeedbackMessages(jobID)
    const indexOffset = isJobRelabeled ? 1 : 0
    const jobVersionResponseFetch = useJobVersionResponse(
        appID,
        jobID,
        selectedVersion - indexOffset
    )

    return useMemo(() => {
        if (!hasValue(jobFetch)) {
            return undefined
        }

        if (downloadResponeData) {
            const responseData = getValue(jobResponseFetch)?.responseData

            return async () => {
                if (
                    responseData !== undefined &&
                    responseData !== "" &&
                    typeof responseData === "string" &&
                    responseData.split(".").length > 1
                ) {
                    return urlSigner(responseData as string).then(res =>
                        downloadData(
                            res,
                            `${
                                jobFetch.value.id
                            }_${selectedVersion}.${responseData
                                .split(".")
                                .pop()}`
                        )
                    )
                }
                dispatch(warnMessage("Output is not available yet."))
                return
            }
        }
        if (imageDownloadPath !== undefined) {
            const imageUrl = findDataUrlForDownload(
                selectedVersion - indexOffset > 0
                    ? getValue(jobVersionResponseFetch)?.response
                    : getValue(jobResponseFetch)?.response,
                imageDownloadPath
            )

            if (imageUrl !== undefined) {
                return async () =>
                    urlSigner(imageUrl).then(res =>
                        downloadData(
                            res,
                            `${jobFetch.value.id}_${selectedVersion}.${imageUrl
                                .split(".")
                                .pop()}`
                        )
                    )
            }
        }

        let fbToDownload: any = []
        let versionSpecificData = {}

        if (hasValue(jobFeedbackMessages)) {
            fbToDownload = prepareFeedbackForDownload(jobFeedbackMessages.value)
        }

        const jobHistory = getValue(jobHistoryFetch)
        if (
            jobHistory !== undefined &&
            jobHistory.history.length > 0 &&
            selectedVersion - indexOffset - 1 < jobHistory.history.length
        ) {
            if (selectedVersion === 0 && isJobRelabeled) {
                // relabeled version
                versionSpecificData = {
                    version: (jobHistory.total + indexOffset + 1).toString(),
                    correct: false,
                    status: JobStatus.IN_CREATION,
                    completed: new Date(),
                    output: null
                }
            } else if (
                (selectedVersion === 0 && !isJobRelabeled) ||
                (selectedVersion === 1 && isJobRelabeled)
            ) {
                // latest version
                versionSpecificData = {
                    version: (jobHistory.total + 1).toString()
                }
            } else {
                // old version
                const { version, correct, status, completed } =
                    jobHistory.history[selectedVersion - indexOffset - 1]
                versionSpecificData = {
                    version,
                    correct,
                    status,
                    completed,
                    output: getValue(jobVersionResponseFetch)?.response
                }
            }
        }

        return async () =>
            downloadAsJSON(
                {
                    ...jobFetch.value,
                    output: hasValue(jobResponseFetch)
                        ? jobResponseFetch.value.response
                        : undefined,
                    messages: fbToDownload,
                    ...versionSpecificData
                },
                `${jobID}__job_data`
            )
    }, [
        jobFetch,
        jobResponseFetch,
        jobFeedbackMessages,
        jobHistoryFetch,
        selectedVersion,
        isJobRelabeled,
        indexOffset,
        jobVersionResponseFetch,
        jobID,
        urlSigner,
        imageDownloadPath,
        downloadResponeData,
        dispatch
    ])
}
